import React, {useMemo} from 'react';
import {Marker, Popup} from "react-leaflet";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import MarkerClusterGroup from 'react-leaflet-markercluster';
import L from 'leaflet';
import { getSafeTelHref, getSafeMailtoHref } from "./filterInstructors";

const customPinIcon = L.divIcon({
    className: "custom-nordic-pin",
    html: `<div style="
        background-color: #285238;
        width: 28px;
        height: 28px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 2px solid #FFFFFF;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
    "><div style="
        width: 8px;
        height: 8px;
        background-color: #FAF8F5;
        border-radius: 50%;
    "></div></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
});

export const InstructorMarkers = ({ instructors = [], onExpressInterest }) => {
    const validInstructors = useMemo(() => {
        if (!Array.isArray(instructors)) return [];
        return instructors.filter(
            i => typeof i.lat === "number" && typeof i.lon === "number" && !isNaN(i.lat) && !isNaN(i.lon)
        );
    }, [instructors]);

    return (
        <MarkerClusterGroup>
            {validInstructors.map(instructor => {
                const telHref = getSafeTelHref(instructor.phone);
                const mailtoHref = getSafeMailtoHref(instructor.email);
                const activeCourses = (instructor.upcomingCourses || []).filter(
                    c => c && c.status !== "completed"
                );
                const nextCourse = activeCourses[0] || null;

                return (
                    <Marker
                        key={instructor.id}
                        position={{lat: instructor.lat, lng: instructor.lon}}
                        icon={customPinIcon}
                    >
                        <Popup minWidth={220}>
                            <Box sx={{ p: 0.5 }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 700,
                                        color: "#285238",
                                        lineHeight: 1.2,
                                        mb: 0.5,
                                    }}
                                >
                                    {instructor.name}
                                </Typography>
                                {instructor.title && (
                                    <Typography variant="caption" sx={{ display: "block", color: "#5B6777", mb: 0.5, fontWeight: 500 }}>
                                        {instructor.title}
                                    </Typography>
                                )}
                                {instructor.place && (
                                    <Typography variant="body2" sx={{ mb: 1, color: "#1E293B" }}>
                                        Sted: <strong>{instructor.place}</strong>
                                    </Typography>
                                )}

                                {nextCourse && (
                                    <Box
                                        sx={{
                                            p: 0.75,
                                            mb: 1,
                                            borderRadius: 1.5,
                                            bgcolor: "rgba(40, 82, 56, 0.06)",
                                            border: "1px solid rgba(40, 82, 56, 0.12)",
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main", display: "block" }}>
                                            Kommende kurs: {nextCourse.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Oppstart: {nextCourse.startDate}
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                                    {typeof onExpressInterest === "function" && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            startIcon={<ContactMailIcon fontSize="small" />}
                                            onClick={() => onExpressInterest(instructor, nextCourse)}
                                            aria-label={`Meld interesse for kurs hos ${instructor.name}`}
                                            sx={{
                                                textTransform: "none",
                                                minHeight: 44,
                                                fontWeight: 700,
                                                bgcolor: "#285238",
                                                color: "#FFFFFF",
                                                "&:hover": { bgcolor: "#1f402c" },
                                                "&:focus-visible": {
                                                    outline: "2px solid #285238",
                                                    outlineOffset: "2px",
                                                },
                                            }}
                                        >
                                            Meld interesse for kurs
                                        </Button>
                                    )}
                                    {telHref && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<PhoneIcon fontSize="small" />}
                                            href={telHref}
                                            aria-label={`Ring ${instructor.name} på ${instructor.phone}`}
                                            sx={{
                                                textTransform: "none",
                                                minHeight: 44,
                                                "&:focus-visible": {
                                                    outline: "2px solid #285238",
                                                    outlineOffset: "2px",
                                                },
                                            }}
                                        >
                                            Ring {instructor.phone}
                                        </Button>
                                    )}
                                    {mailtoHref && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<EmailIcon fontSize="small" />}
                                            href={mailtoHref}
                                            aria-label={`Send e-post til ${instructor.name}`}
                                            sx={{
                                                textTransform: "none",
                                                minHeight: 44,
                                                "&:focus-visible": {
                                                    outline: "2px solid #285238",
                                                    outlineOffset: "2px",
                                                },
                                            }}
                                        >
                                            Send e-post
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Popup>
                    </Marker>
                );
            })}
        </MarkerClusterGroup>
    );
};